import ClearTrackService from '../services/service';
import * as Constants from '../constants/constants';

const ENTITY_LABELS = {
    song: 'Song', 
    performer: 'Performer',
    writer: 'Writer',
    licensor: 'Licensor',
    cueSheetPublisher: 'Cue Sheet Publisher',
};

const ENTITY_ORDER = { song: 0, performer: 1, writer: 2, licensor: 3, cueSheetPublisher: 4 };
const ENTITY_SEARCH = { writer: 'WRITER', performer: 'PERFORMER', licensor: 'LICENSOR' };
const ENTITY_ID_FIELDS = { writer: 'writer_id', performer: 'performer_id', licensor: 'licensor_id' };
const WARNING_NOTES_EDIT_ROLES = ['Super Admin', 'Music Clearance (Admin)'];
const CLEARANCE_ORIGINS = ['fromPerformerEdit', 'clearanceRecord', 'fromMainTitleOrClearance'];

export const emptyWarningNotesModal = { open: false, title: 'Warning', warningLines: [], warningGroups: [] };

export const shouldShowProfileLookupWarningPopup = (origin) => !CLEARANCE_ORIGINS.includes(origin);
const hasWarningNotes = (notes) => !!(notes && String(notes).trim());
export const canEditWarningNotes = (userContext) =>
    (userContext?.active_tenant?.division_role || []).some((role) => WARNING_NOTES_EDIT_ROLES.includes(role?.role_name));

const beginWarningNotesRequest = (component) => {
    component.warningNotesRequestId = (component.warningNotesRequestId || 0) + 1;
    return component.warningNotesRequestId;
};

const isWarningNotesRequestCurrent = (component, requestId) =>
    requestId == null || component.warningNotesRequestId === requestId;

export const closeWarningNotesModal = (component) => {
    component.setState({ warningNotesModal: { ...emptyWarningNotesModal } });
};

const formatLine = (entityKey, entityName, warningNotes) => ({
    entityLabel: ENTITY_LABELS[entityKey] || entityKey,
    entityName: entityName || '',
    warningNotes: String(warningNotes).trim(),
});

const buildModalState = (entries = []) => {
    const validEntries = entries.filter((entry) => hasWarningNotes(entry.warningNotes));
    if (!validEntries.length) return { ...emptyWarningNotesModal };

    if (validEntries.some((entry) => entry.songId)) {
        const groupMap = new Map();
        validEntries.forEach((entry) => {
            const key = String(entry.songId);
            if (!groupMap.has(key)) {
                groupMap.set(key, { songTitle: entry.songTitle || '', entries: [] });
            }
            groupMap.get(key).entries.push(entry);
        });
        const warningGroups = [...groupMap.values()]
            .map(({ songTitle, entries: groupEntries }) => ({
                songTitle,
                warningLines: groupEntries
                    .sort((a, b) => (ENTITY_ORDER[a.entityKey] ?? 99) - (ENTITY_ORDER[b.entityKey] ?? 99))
                    .map((entry) => formatLine(entry.entityKey, entry.entityName, entry.warningNotes)),
            }))
            .filter((group) => group.warningLines.length);

        return warningGroups.length
            ? { open: true, title: 'Warning', warningLines: [], warningGroups }
            : { ...emptyWarningNotesModal };
    }

    const warningLines = validEntries
        .sort((a, b) => (ENTITY_ORDER[a.entityKey] ?? 99) - (ENTITY_ORDER[b.entityKey] ?? 99))
        .map((entry) => formatLine(entry.entityKey, entry.entityName, entry.warningNotes));

    return { open: true, title: 'Warning', warningLines, warningGroups: [] };
};

const addEntity = (entities, seen, entityKey, entityId, entityName, warningNotes, affiliationName = null, affiliationId = null, songId = null, songTitle = null) => {
    if (!entityId) return;
    const key = songId ? `${songId}:${entityKey}:${entityId}` : `${entityKey}:${entityId}`;
    if (seen.has(key)) return;
    seen.add(key);
    entities.push({
        entityKey,
        entityId,
        entityName: entityName || '',
        warningNotes: warningNotes || null,
        affiliationName: affiliationName || null,
        affiliationId: affiliationId || null,
        songId,
        songTitle,
    });
};

const getWriterDisplayName = (writer) => {
    const name = writer?.writer_name || '';
    return writer?.affiliation_name ? `${name} (${writer.affiliation_name})` : name;
};

const buildEntitiesFromSongDetails = (songDetails, filter = {}) => {
    const entities = [];
    const seen = new Set();
    const writerIds = filter.writerIds?.map(String);
    const songId = songDetails?.song_id || null;
    const songTitle = songDetails?.song_title || null;

    if (songDetails?.song_id) {
        addEntity(entities, seen, 'song', songDetails.song_id, songDetails.song_title, songDetails.warning_notes, null, null, songId, songTitle);
    }

    (songDetails?.performers_list || []).forEach((item) => {
        if (filter.performerId && String(item.performer_id) !== String(filter.performerId)) return;
        addEntity(entities, seen, 'performer', item.performer_id, item.performer_name, item.warning_notes, null, null, songId, songTitle);
    });

    (songDetails?.writers_list || []).forEach((item) => {
        const writerId = item.writer_id ?? item.writer;
        if (!writerId) return;
        if (writerIds?.length && !writerIds.includes(String(writerId))) return;
        addEntity(
            entities,
            seen,
            'writer',
            writerId,
            getWriterDisplayName(item),
            item.warning_notes,
            item.affiliation_name || null,
            item.affiliation_id || null,
            songId,
            songTitle
        );
    });

    (songDetails?.licensors_list || []).forEach((licensor) => {
        addEntity(entities, seen, 'licensor', licensor.s_licensor_id, licensor.licensor_name, licensor.warning_notes, null, null, songId, songTitle);
        (licensor.cue_sheet_publisher_list || []).forEach((publisher) => {
            addEntity(entities, seen, 'cueSheetPublisher', publisher.cue_sheet_publisher_id, publisher.cue_sheet_publisher_name, publisher.warning_notes, null, null, songId, songTitle);
        });
    });

    return entities;
};


const stripSearchName = (name) => {
    const trimmed = String(name || '').trim();
    const parenIdx = trimmed.indexOf('(');
    if (parenIdx > 0) return trimmed.substring(0, parenIdx).trim();
    const hyphenIdx = trimmed.indexOf('-');
    if (hyphenIdx > 0) return trimmed.substring(0, hyphenIdx).trim();
    return trimmed;
};

const resolveEntityWarningNotes = async (
    entityKey,
    entityId,
    entityName,
    warningNotes,
    tenantId,
    affiliationName = null,
    affiliationId = null
) => {
    if (hasWarningNotes(warningNotes)) return warningNotes;
    if (!entityId || !tenantId) return null;

    if (entityKey === 'song') {
        try {
            const response = await ClearTrackService.getData(
                `${Constants.ClearTrackServiceBaseUrl}/songDetails?song_id=${entityId}`,
                tenantId
            );
            const notes = response?.data?.[0]?.warning_notes;
            if (hasWarningNotes(notes)) return notes;
        } catch (err) {
            return null;
        }
    }

    const searchType = ENTITY_SEARCH[entityKey];
    const searchName = stripSearchName(entityName);

    if (searchType && searchName) {
        try {
            const response = await ClearTrackService.getData(
                `${Constants.ClearTrackServiceBaseUrl}/entitySearch?entity=${searchType}&searchString=${encodeURIComponent(searchName)}`,
                tenantId
            );
            const idField = ENTITY_ID_FIELDS[entityKey];
            const results = response?.data || [];
            const idMatch = results.find((item) => String(item[idField]) === String(entityId));
            if (hasWarningNotes(idMatch?.warning_notes)) return idMatch.warning_notes;

            if (entityKey === 'writer') {
                const affFromName = entityName?.match(/\(([^)]+)\)/)?.[1]?.trim();
                const affToMatch = affiliationName || affFromName;

                if (affiliationId) {
                    const affIdMatch = results.find(
                        (item) =>
                            String(item.affiliation_id) === String(affiliationId) &&
                            hasWarningNotes(item.warning_notes)
                    );
                    if (affIdMatch) return affIdMatch.warning_notes;
                }

                if (affToMatch) {
                    const normalizedAff = affToMatch.toUpperCase();
                    const affMatch = results.find(
                        (item) =>
                            String(item.affiliation_name || '').toUpperCase() === normalizedAff &&
                            hasWarningNotes(item.warning_notes)
                    );
                    if (affMatch) return affMatch.warning_notes;
                }

                if (results.length === 1 && hasWarningNotes(results[0].warning_notes)) {
                    return results[0].warning_notes;
                }
            }
        } catch (err) {
            // continue to fallback lookups
        }
    }

    try {
        if (entityKey === 'performer') {
            const response = await ClearTrackService.getData(`${Constants.ClearTrackServiceBaseUrl}/staticData?staticTable=lu_performer`, tenantId);
            const match = response?.data?.find((item) => String(item.id) === String(entityId));
            return hasWarningNotes(match?.warning_notes) ? match.warning_notes : null;
        }

        if (entityKey === 'cueSheetPublisher') {
            const response = await ClearTrackService.getData(
                `${Constants.ClearTrackServiceBaseUrl}/staticData?staticTable=lu_cue_sheet_publisher&tableId=${entityId}`,
                tenantId
            );
            const match = response?.data?.[0] || response?.data?.find?.((item) => String(item.id) === String(entityId));
            return hasWarningNotes(match?.warning_notes) ? match.warning_notes : null;
        }

        if (entityKey === 'licensor') {
            const response = await ClearTrackService.getData(`${Constants.ClearTrackServiceBaseUrl}/licensor?licensor_id=${entityId}`, tenantId);
            return hasWarningNotes(response?.data?.[0]?.warning_notes) ? response.data[0].warning_notes : null;
        }
    } catch (err) {
        return null;
    }

    return null;
};

const showCombinedWarningNotesModal = async (component, entities, tenantId, requestId, onShown) => {
    if (!entities?.length || !tenantId) return;

    const resolvedEntries = await Promise.all(
        entities.map(async (entity) => {
            const warningNotes = await resolveEntityWarningNotes(
                entity.entityKey,
                entity.entityId,
                entity.entityName,
                entity.warningNotes,
                tenantId,
                entity.affiliationName,
                entity.affiliationId
            );
            if (!hasWarningNotes(warningNotes)) return null;
            return {
                entityKey: entity.entityKey,
                entityId: entity.entityId,
                entityName: entity.entityName,
                warningNotes,
                songId: entity.songId,
                songTitle: entity.songTitle,
            };
        })
    );

    if (!isWarningNotesRequestCurrent(component, requestId)) return;
    const validEntries = resolvedEntries.filter(Boolean);
    const modalState = buildModalState(validEntries);
    if (modalState.open) {
        component.setState({ warningNotesModal: modalState }, () => {
            if (onShown) onShown(validEntries);
        });
    } else if (onShown) {
        onShown(validEntries);
    }
};

export const registerShownWarningEntities = (component, entries = []) => {
    if (!component.shownWarningEntities) component.shownWarningEntities = new Set();
    entries.forEach((entry) => {
        if (entry?.entityKey && entry?.entityId) {
            component.shownWarningEntities.add(`${entry.entityKey}:${entry.entityId}`);
        }
    });
};

export const isWarningEntityAlreadyShown = (component, entityKey, entityId) =>
    component.shownWarningEntities?.has(`${entityKey}:${entityId}`);

export const cancelPendingWarningNotesRequests = (component) => beginWarningNotesRequest(component);


export const showEntityWarningNotesWithLookupIfNeeded = async (component, params) => {
    const { entityKey, entityId, entityName, warningNotes, tenantId, affiliationName, affiliationId } = params;
    const requestId = beginWarningNotesRequest(component);
    const resolvedNotes = await resolveEntityWarningNotes(
        entityKey,
        entityId,
        entityName,
        warningNotes,
        tenantId,
        affiliationName,
        affiliationId
    );
    if (!isWarningNotesRequestCurrent(component, requestId)) return false;
    if (!entityId || !hasWarningNotes(resolvedNotes)) return false;
    checkEntityWarningNotes(component, { entityKey, entityId, entityName, warningNotes: resolvedNotes, requestId });
    return true;
};

export const checkEntityWarningNotes = (component, { entityKey, entityId, entityName, warningNotes, requestId }) => {
    if (!entityId || !hasWarningNotes(warningNotes) || !isWarningNotesRequestCurrent(component, requestId)) return;
    component.setState({ warningNotesModal: buildModalState([{ entityKey, entityName, warningNotes }]) });
};

export const showEntityWarningNotesWithLookup = async (component, { entityKey, entityId, entityName, warningNotes, tenantId, affiliationName, affiliationId }) => {
    const requestId = beginWarningNotesRequest(component);
    const resolvedNotes = await resolveEntityWarningNotes(
        entityKey,
        entityId,
        entityName,
        warningNotes,
        tenantId,
        affiliationName,
        affiliationId
    );
    if (!isWarningNotesRequestCurrent(component, requestId)) return;
    checkEntityWarningNotes(component, { entityKey, entityId, entityName, warningNotes: resolvedNotes, requestId });
};

export const showCombinedSongWarnings = (component, { songId, songDetails, tenantId, filter = {}, onShown }) => {
    if (!tenantId || (!songDetails && !songId)) return;
    const requestId = beginWarningNotesRequest(component);
    if (songDetails) {
        return showCombinedWarningNotesModal(
            component,
            buildEntitiesFromSongDetails(songDetails, filter),
            tenantId,
            requestId,
            onShown
        );
    }
    return fetchSongDetailsAndShowCombinedWarnings(component, songId, tenantId, requestId, filter, onShown);
};

export const showCombinedSongWarningsOnce = (component, { songId, songDetails, tenantId, filter = {}, flag = 'combinedWarningNotesShown' }) => {
    if (!tenantId || component[flag]) return;
    if (!songDetails && !songId) return;
    component[flag] = true;
    const requestId = beginWarningNotesRequest(component);
    if (songDetails) {
        return showCombinedWarningNotesModal(
            component,
            buildEntitiesFromSongDetails(songDetails, filter),
            tenantId,
            requestId
        );
    }
    return fetchSongDetailsAndShowCombinedWarnings(component, songId, tenantId, requestId, filter);
};

const fetchSongDetailsAndShowCombinedWarnings = async (component, songId, tenantId, requestId, filter = {}, onShown) => {
    if (!songId || !tenantId) return;
    try {
        const response = await ClearTrackService.getData(`${Constants.ClearTrackServiceBaseUrl}/songDetails?song_id=${songId}`, tenantId);
        if (!isWarningNotesRequestCurrent(component, requestId)) return;
        await showCombinedWarningNotesModal(component, buildEntitiesFromSongDetails(response?.data?.[0], filter), tenantId, requestId, onShown );
    } catch (err) {
        return;
    }
};

export const loadWriterWarningNotes = async (writerId, writerName, tenantId, existingNotes = null, affiliationName = null, affiliationId = null) => {
    return resolveEntityWarningNotes('writer', writerId, writerName, existingNotes, tenantId, affiliationName, affiliationId);
};

export const showCombinedEntityWarningNotes = (component, entities, tenantId) => {
    if (!tenantId || !entities?.length) return;
    const requestId = beginWarningNotesRequest(component);
    return showCombinedWarningNotesModal(component, entities, tenantId, requestId);
};